import { Tooltip, Typography } from "antd";
import { ConfigType } from "dayjs";
import { useTenantTimezone } from "@hooks";
import { getTimezoneAbbreviation, getWorldClockTimes } from "@/components/timezone";

interface Props {
  date: ConfigType;
  format?: string;
}

/**
 * Renders an exam term date/time in the tenant timezone, annotated with the
 * region label and timezone abbreviation (e.g. "(Saudi Arabia, AST)"), plus a
 * hover tooltip showing the same instant in major cities (Zagreb, London, ...).
 */
export const ExamTermTime = ({ date, format = "D.MM.YYYY H:mm" }: Props) => {
  const { iana, label, format: formatInTenantTz } = useTenantTimezone();

  if (!date) {
    return <>-</>;
  }

  const text = formatInTenantTz(date, format);
  const abbr = getTimezoneAbbreviation(date, iana);
  const suffix = [label, abbr].filter(Boolean).join(", ");
  const worldClock = getWorldClockTimes(date, format);

  const tooltip = (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {worldClock.map((city) => (
        <span key={city.label} style={{ whiteSpace: "nowrap" }}>
          {city.label}: {city.time}
          {city.abbr ? ` ${city.abbr}` : ""}
        </span>
      ))}
    </div>
  );

  return (
    <Tooltip title={tooltip}>
      <Typography.Text style={{ cursor: "help", borderBottom: "1px dotted currentColor" }}>
        {text}
        {suffix ? ` (${suffix})` : ""}
      </Typography.Text>
    </Tooltip>
  );
};
